/**
 * Вопрос клиента у команды (client-questions): переписка, ответ с вложениями. Клиент видит ответы
 * за подписью «Центрика».
 */
import { useEffect, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@/app/auth-context'
import { teamApi, type QuestionDetail } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  QuestionReplyForm,
  QuestionStatusBadge,
  QuestionThread,
  formatDateTime,
} from '@/questions/question-ui'

export function TeamQuestionPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { refresh } = useAuth()
  const [question, setQuestion] = useState<QuestionDetail | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    const controller = new AbortController()
    setQuestion(null)
    setError(null)
    teamApi.questions
      .get(id, controller.signal)
      .then((q) => {
        if (!controller.signal.aborted) setQuestion(q)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить вопрос'))
      })
    return () => controller.abort()
  }, [id])

  return (
    <div>
      <div className="mb-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate('/team/questions')}
          leftIcon={<ArrowLeft className="size-4 stroke-current" aria-hidden />}
        >
          К вопросам клиентов
        </Button>
      </div>
      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : !question ? (
        <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
      ) : (
        <>
          <PageHead
            className="mb-4 flex-wrap"
            title={
              <span className="flex flex-wrap items-center gap-2.5">
                <span className="break-words">{question.subject}</span>
                <QuestionStatusBadge status={question.status} side="team" />
              </span>
            }
            subtitle={
              <>
                Вопрос № {question.number} от {formatDateTime(question.createdAt)} ·{' '}
                {question.clientId ? (
                  <RouterLink to={`/team/clients/${question.clientId}`} className="text-accent hover:underline">
                    {question.clientName}
                  </RouterLink>
                ) : null}
                {question.orderId ? (
                  <>
                    {' · '}
                    <RouterLink to={`/team/orders/${question.orderId}`} className="text-accent hover:underline">
                      заказ № {question.orderNumber}
                    </RouterLink>
                  </>
                ) : null}
              </>
            }
          />
          <QuestionThread question={question} side="team" />
          {question.canReply ? (
            <Card header="Ответить клиенту" className="mt-4">
              <QuestionReplyForm
                label="Ответ"
                submitLabel="Отправить ответ"
                placeholder="Клиент увидит ответ за подписью «Центрика» и получит письмо"
                send={(body, files) => teamApi.questions.reply(question.id, body, files)}
                onSent={(next) => {
                  setQuestion(next)
                  // Счётчик у «Вопросов клиентов».
                  void refresh()
                }}
              />
            </Card>
          ) : (
            <Alert variant="info" className="mt-4">
              Клиент закрыл вопрос — отвечать в него нельзя.
            </Alert>
          )}
        </>
      )}
    </div>
  )
}
